import { create } from "zustand";
import { persist } from "zustand/middleware";

type OverdueToastState = "active" | "dismissed";
type ScopedOverdueToasts = Record<string, Record<string, OverdueToastState>>;

interface PatientStatusStore {
  checklistVisibility: Record<string, boolean>;
  checklistVisibilityDefaults: Record<string, boolean>;
  setChecklistVisibility: (
    scope: string,
    name: string,
    visible: boolean,
  ) => void;
  setAllChecklistVisibility: (scope: string, visible: boolean) => void;
  overdueToasts: ScopedOverdueToasts;
  setOverdueToastState: (
    scope: string,
    key: string,
    state: OverdueToastState,
  ) => void;
  /** Mark this scope's "active" toasts as "dismissed". Returns their row keys. */
  dismissAllActiveToasts: (scope: string) => string[];
  /** Remove this scope's stale row state. Returns the row keys that were removed. */
  cleanupOverdueToasts: (scope: string, activeToastKeys: string[]) => string[];
}

export function patientStatusVisibilityScope(
  tenantSubDomain: string | undefined,
  userId: string | undefined,
) {
  return tenantSubDomain && userId
    ? JSON.stringify([tenantSubDomain, userId])
    : null;
}

export function patientStatusVisibilityKey(scope: string, name: string) {
  return JSON.stringify([scope, name]);
}

function isChecklistVisibilityKeyForScope(key: string, scope: string) {
  try {
    const parts: unknown = JSON.parse(key);
    return Array.isArray(parts) && parts.length === 2 && parts[0] === scope;
  } catch {
    return false;
  }
}

export function isPatientStatusChecklistVisible(
  checklistVisibility: Record<string, boolean>,
  checklistVisibilityDefaults: Record<string, boolean>,
  scope: string,
  name: string,
) {
  return (
    checklistVisibility[patientStatusVisibilityKey(scope, name)] ??
    checklistVisibilityDefaults[scope] ??
    true
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPatientStatusScope(scope: unknown): scope is string {
  if (typeof scope !== "string") return false;

  try {
    const parts: unknown = JSON.parse(scope);
    return (
      Array.isArray(parts) &&
      parts.length === 2 &&
      parts.every((part) => typeof part === "string" && part.length > 0)
    );
  } catch {
    return false;
  }
}

function getScopedChecklistVisibility(
  persistedState: unknown,
): Record<string, boolean> {
  if (!isRecord(persistedState)) return {};
  const persistedVisibility = persistedState.checklistVisibility;
  if (!isRecord(persistedVisibility)) return {};

  const scopedVisibility: Record<string, boolean> = {};
  for (const [key, visible] of Object.entries(persistedVisibility)) {
    if (typeof visible !== "boolean") continue;

    try {
      const keyParts: unknown = JSON.parse(key);
      if (
        Array.isArray(keyParts) &&
        keyParts.length === 2 &&
        isPatientStatusScope(keyParts[0]) &&
        typeof keyParts[1] === "string" &&
        keyParts[1].length > 0
      ) {
        scopedVisibility[key] = visible;
      }
    } catch {
      // Legacy visibility keys were unscoped and must not cross users/tenants.
    }
  }
  return scopedVisibility;
}

export function migratePatientStatusStore(
  persistedState: unknown,
  persistedVersion: number,
) {
  return {
    // Version 0 visibility used global checklist keys. Version 1 keys are
    // tenant+user scoped, and are preserved only when their shape proves it.
    checklistVisibility:
      persistedVersion >= 1 ? getScopedChecklistVisibility(persistedState) : {},
    checklistVisibilityDefaults: {},
    // Versions 0 and 1 stored row/event dismissals globally. None of those
    // entries can be attributed to a tenant+user scope safely, so reset them.
    overdueToasts: {} as ScopedOverdueToasts,
  };
}

export const usePatientStatusStore = create<PatientStatusStore>()(
  persist(
    (set) => ({
      checklistVisibility: {},
      checklistVisibilityDefaults: {},
      setChecklistVisibility: (scope, name, visible) =>
        set((state) => ({
          checklistVisibility: {
            ...state.checklistVisibility,
            [patientStatusVisibilityKey(scope, name)]: visible,
          },
        })),
      setAllChecklistVisibility: (scope, visible) =>
        set((state) => ({
          // All/None replaces every exact-name override in this scope,
          // including names that are not active in the panel right now.
          checklistVisibility: Object.fromEntries(
            Object.entries(state.checklistVisibility).filter(
              ([key]) => !isChecklistVisibilityKeyForScope(key, scope),
            ),
          ),
          checklistVisibilityDefaults: {
            ...state.checklistVisibilityDefaults,
            [scope]: visible,
          },
        })),
      overdueToasts: {},
      setOverdueToastState: (scope, key, toastState) =>
        set((state) => ({
          overdueToasts: {
            ...state.overdueToasts,
            [scope]: {
              ...state.overdueToasts[scope],
              [key]: toastState,
            },
          },
        })),
      dismissAllActiveToasts: (scope) => {
        const activeKeys: string[] = [];
        set((state) => {
          const scopedToasts = state.overdueToasts[scope];
          if (!scopedToasts) return state;

          const updated = { ...scopedToasts };
          for (const [key, val] of Object.entries(updated)) {
            if (val === "active") {
              updated[key] = "dismissed";
              activeKeys.push(key);
            }
          }
          if (activeKeys.length === 0) return state;
          return {
            overdueToasts: { ...state.overdueToasts, [scope]: updated },
          };
        });
        return activeKeys;
      },
      cleanupOverdueToasts: (scope, activeToastKeys) => {
        const activeKeySet = new Set(activeToastKeys);
        const removedKeys: string[] = [];
        set((state) => {
          const scopedToasts = state.overdueToasts[scope];
          if (!scopedToasts) return state;

          const cleaned: Record<string, OverdueToastState> = {};
          for (const [key, val] of Object.entries(scopedToasts)) {
            if (activeKeySet.has(key)) {
              cleaned[key] = val;
            } else {
              removedKeys.push(key);
            }
          }
          if (removedKeys.length === 0) return state;

          const overdueToasts = { ...state.overdueToasts };
          if (Object.keys(cleaned).length > 0) {
            overdueToasts[scope] = cleaned;
          } else {
            delete overdueToasts[scope];
          }
          return { overdueToasts };
        });
        return removedKeys;
      },
    }),
    {
      name: "patient-status-store",
      version: 2,
      migrate: migratePatientStatusStore,
      partialize: (state: PatientStatusStore) => ({
        checklistVisibility: state.checklistVisibility,
        checklistVisibilityDefaults: state.checklistVisibilityDefaults,
        overdueToasts: state.overdueToasts,
      }),
    },
  ),
);
